import CornerstoneLogo from '@/assets/logos/CornerstoneLogo';
import GoGovLogo from '@/go.gov/components/layout/GOGovLogo';
import { HEADER_HEIGHT } from '@/ui/Layout/Header/HeaderBar';
import { IS_ETS_NEXT } from '@/utils/constants';
import useGet from '@/utils/hooks/api/vercel/useGet';
import useSession from '@/utils/hooks/useSession';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  '@keyframes fadeSlide': {
    '0%': { transform: 'translateX(-20px)', opacity: 0 },
    '100%': { transform: 'translateX(0)', opacity: 1 },
  },
  fadeSlide: {
    animation: 'fadeSlide 1s ease-out',
  },
  logoWrapper: {
    margin: '8px',
    height: HEADER_HEIGHT - 5,
  },
  logoWrapperV2: {
    margin: '8px',
    height: HEADER_HEIGHT - 5,
  },
}));

// Helper to determine if the logo is default
function isDefaultLogo(logo) {
  if (!logo) return true;
  const oldDefault =
    process.env.NEXT_PUBLIC_STATIC_ASSETS_URL + 'cis-logo-color.png';
  const newDefault = '/images/CornerstoneLogoColor.png';
  return [oldDefault, newDefault].includes(logo);
}

function Logo() {
  const { classes } = useStyles();
  const router = useRouter();
  const { org } = useSession();

  // Memoize to avoid recalculating on every render
  const isDefault = useMemo(() => isDefaultLogo(org.logo), [org.logo]);

  const { data: orgLogo, isLoading } = useGet(`/organizations/logo`, {
    fireOnMount: !isDefault,
    skip: isDefault,
  });

  if (IS_ETS_NEXT) {
    return (
      <Button
        aria-label='Go to hub homepage'
        onClick={() => router.push('/hub')}
        sx={{
          minWidth: 0,
          p: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <GoGovLogo />
      </Button>
    );
  }

  if (!isDefault && isLoading) {
    return (
      <Skeleton
        variant='rectangular'
        animation='wave'
        width={210}
        height={HEADER_HEIGHT}
        className={`${classes.logoWrapper} ${classes.fadeSlide}`}
        style={{ marginLeft: 14 }}
      />
    );
  }

  if (!isDefault && orgLogo) {
    return (
      <Image
        className={classes.logoWrapper}
        width={210}
        height={HEADER_HEIGHT - 16}
        src={orgLogo?.url}
        alt='Site Logo'
        style={{
          objectFit: 'contain',
          objectPosition: 'left',
          marginLeft: 14,
        }}
        priority
      />
    );
  }

  return (
    <CornerstoneLogo
      className={classes.logoWrapper}
      colors={{
        primary: '#040059',
        secondary: '#ff8806',
        tertiary: '#4f88fc',
      }}
    />
  );
}

export default Logo;
